import type {
  PlayerSummaryStats,
  ReportingPlayer
} from '@frontend/core/lib/domains/reporting/types'
import type { UnitSystem } from '@frontend/core/lib/domains/units/types'
import type { RefObject } from 'react'
import { formatStatValue, getColumnUnitLabel } from '../../lib/format-cell'
import type { ColumnDef } from '../../lib/sport-config'
import { getStatByAccessor } from '../../lib/stat-utils'
import type { DrillRow } from './formatter'

interface PlayerPrintProps {
  player: ReportingPlayer
  rows: DrillRow[]
  totals: PlayerSummaryStats | null
  totalDuration: string
  columns: ColumnDef[]
  groupLabel: string
  tabLabel: string
  unitSystem: UnitSystem
  pitchImageRef?: RefObject<HTMLImageElement | null>
}

/**
 * Print-only player stats layout matching the Angular reporting tool's PDF output.
 * Hidden on screen, shown only when printing.
 */
export function PlayerPrint({
  player,
  rows,
  totals,
  totalDuration,
  columns,
  groupLabel,
  tabLabel,
  unitSystem,
  pitchImageRef
}: PlayerPrintProps) {
  return (
    <div className='hidden print:block' style={{ color: '#000' }}>
      {/* Player info */}
      <div style={{ marginBottom: '1rem' }}>
        <h2
          style={{
            fontSize: '4mm',
            fontWeight: 'bold',
            marginBottom: '0.25rem'
          }}
        >
          {player.displayName || `${player.firstName} ${player.lastName}`}
          {player.number != null ? ` #${player.number}` : ''}
          {player.position ? ` — ${player.position}` : ''}
        </h2>
        <div style={{ fontSize: '3mm' }}>
          {groupLabel} &rsaquo; {tabLabel}
        </div>
      </div>

      {/* Stats table */}
      {rows.length > 0 && (
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '3mm'
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  textAlign: 'left',
                  borderBottom: '1px solid #999',
                  padding: '2px 4px'
                }}
              >
                Period
              </th>
              <th
                style={{
                  textAlign: 'left',
                  borderBottom: '1px solid #999',
                  padding: '2px 4px'
                }}
              >
                Duration
              </th>
              {columns.map((col) => {
                const unit = getColumnUnitLabel(col.unitType, unitSystem)
                return (
                  <th
                    key={col.accessor}
                    style={{
                      textAlign: 'right',
                      borderBottom: '1px solid #999',
                      padding: '2px 4px'
                    }}
                  >
                    {col.label}
                    {unit ? ` (${unit})` : ''}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td
                  style={{
                    padding: '2px 4px',
                    borderBottom: '1px solid #ddd'
                  }}
                >
                  {row.name}
                </td>
                <td
                  style={{
                    padding: '2px 4px',
                    borderBottom: '1px solid #ddd',
                    fontVariantNumeric: 'tabular-nums'
                  }}
                >
                  {row.duration}
                </td>
                {columns.map((col) => (
                  <td
                    key={col.accessor}
                    style={{
                      textAlign: 'right',
                      padding: '2px 4px',
                      borderBottom: '1px solid #ddd',
                      fontVariantNumeric: 'tabular-nums'
                    }}
                  >
                    {formatStatValue(
                      getStatByAccessor(row.stats, col.accessor),
                      col.unitType,
                      unitSystem
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {totals && (
            <tfoot>
              <tr>
                <td
                  style={{
                    padding: '2px 4px',
                    fontWeight: 'bold',
                    borderTop: '2px solid #999'
                  }}
                >
                  Totals
                </td>
                <td
                  style={{
                    padding: '2px 4px',
                    fontWeight: 'bold',
                    borderTop: '2px solid #999',
                    fontVariantNumeric: 'tabular-nums'
                  }}
                >
                  {totalDuration}
                </td>
                {columns.map((col) => (
                  <td
                    key={col.accessor}
                    style={{
                      textAlign: 'right',
                      padding: '2px 4px',
                      fontWeight: 'bold',
                      borderTop: '2px solid #999',
                      fontVariantNumeric: 'tabular-nums'
                    }}
                  >
                    {formatStatValue(
                      getStatByAccessor(totals, col.accessor),
                      col.unitType,
                      unitSystem
                    )}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      )}

      {/* Pitch map snapshot — src kept in sync via useEffect in parent */}
      <div style={{ marginTop: '1rem' }}>
        <img
          ref={pitchImageRef}
          alt='Pitch map'
          style={{ width: '100%', maxHeight: '45vh', objectFit: 'contain' }}
        />
      </div>
    </div>
  )
}
